<i18n src="./resources/locales.json"></i18n>
<i18n src="@/mixins/ecs-time-formatter/resources/locales.json"></i18n>

<template>
  <EcsNotificationsPanel
    :open="open"
    :panel-name="$t('callbacks')"
    :items="items"
    is-callbacks-panel
    @onItemClick="onItemClick"
  >
    <template #fab>
      <v-btn :disabled="isOfflineModeEnabled" color="primary" fab x-small @click="createNewCallback">
        <v-tooltip color="surface lighten-1" left>
          <template v-slot:activator="{ on }">
            <v-icon v-on="on">
              mdi-calendar
            </v-icon>
          </template>
          <span v-once>
            {{ $t('schedule_callback') }}
          </span>
        </v-tooltip>
      </v-btn>
    </template>
  </EcsNotificationsPanel>
</template>

<script>
import moment from 'moment-timezone';
import { mapGetters, mapActions } from 'vuex';
import EcsNotificationsPanel from '@/components/ecs-notifications-panel/EcsNotificationsPanel';
import InteractionService from '@/services/interactionService';
import referenceDataService from '@/services/referenceDataService';
import { COMPLETION_STATUS } from '@/constants/interaction/constants';
import EcsTimeFormatter from '@/mixins/ecs-time-formatter/EcsTimeFormatter';

export default {
  name: 'EcsCallbacksPanel',

  components: {
    EcsNotificationsPanel
  },

  mixins: [EcsTimeFormatter],

  data() {
    return {
      open: false,
      apiCallbacks: [],
      updateIntervalRef: null
    };
  },

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent']),

    ...mapGetters('CONTACTS', ['getAgentByEndPoint', 'getAgents', 'getSupervisors']),

    ...mapGetters('NOTIFICATIONS', ['getCallbacks']),

    ...mapGetters('INTERACTIONS', ['getInteraction']),

    ...mapGetters('SETTINGS', ['isOfflineModeEnabled', 'applicationSettings', 'currentUserSettings']),

    ...mapGetters('REFERENCES', ['availableTimeZones']),

    ...mapGetters('ORGANIZATION', ['organizationData', 'configurationVersion']),

    ...mapGetters('EVENT_TIMING', ['isWaitingForEvent']),

    items() {
      return this.getCallbacks;
    },

    language() {
      return this.applicationSettings.language;
    },

    timeZone() {
      return this.applicationSettings.timeZone;
    }
  },

  watch: {
    open(isOpen) {
      if (isOpen) {
        if (this.getAgents.length === 0 || this.getSupervisors.length === 0) {
          this.requestAgentsSupervisorsContacts();
        }
      }
    },

    language() {
      this.getAndCreateAllCallbacks();
    },

    timeZone() {
      this.getAndCreateAllCallbacks();
    },

    availableTimeZones() {
      this.getAndCreateAllCallbacks();
    }
  },

  sockets: {
    CALLBACK_UPDATED_EVENT(callback) {
      this.updateCallbacks(callback, () => {
        this.getAndCreateAllCallbacks();
      });
    }
  },

  mounted() {
    this.updateIntervalRef = setInterval(this.updateExistingCallbacks, 60000);
    this.getAndCreateAllCallbacks();
  },

  beforeDestroy() {
    clearInterval(this.updateIntervalRef);
  },

  methods: {
    ...mapActions('CONTACTS', ['requestAgentsSupervisorsContacts']),

    ...mapActions('AGENT', ['retrieveAgentCallbacks']),

    ...mapActions('NOTIFICATIONS', ['setCallbacks']),

    ...mapActions('EVENT_TIMING', ['addEventTimingConstraint', 'removeEventTimingConstraint']),

    setOpen(value) {
      this.open = value;
    },

    isOpened() {
      return this.open;
    },

    updateExistingCallbacks() {
      this.sortAndStoreCallbacks(this.apiCallbacks);
    },

    async updateCallbacks(event, completedCallback) {
      const callbacks = this.getCallbacks;
      const index = callbacks.findIndex(cb => cb.interactionId === event.interactionId);

      if (index !== -1) {
        const agentAssignedTeamIndex = await referenceDataService
          .getOrgConfiguration(this.configurationVersion)
          .then(response => {
            const currentBp = response.data.businessProcesses.find(bp => bp.id === event.businessProcessId);
            return currentBp.teams.findIndex(team => team.agents.find(agent => (agent.id = this.loggedInAgent.id)));
          });
        if (
          (event.assignedAgentId !== this.loggedInAgent.id && agentAssignedTeamIndex === -1) ||
          event.interactionEnded !== 'NotEnded' ||
          event.pendingStatusId === COMPLETION_STATUS.delegateToSupervisor ||
          event.completionStatus === COMPLETION_STATUS.cancel
        ) {
          callbacks.splice(index, 1);
        } else {
          callbacks.splice(index, 1, event);
        }
      } else if (event.assignedAgentId === this.loggedInAgent.id && event.interactionEnded !== 'Purged') {
        callbacks.push(event);
      }

      this.sortAndStoreCallbacks(callbacks);
      completedCallback();
    },

    /**
     * Gets the Callbacks from ECS and creates the callback items
     */
    getAndCreateAllCallbacks() {
      return this.retrieveAgentCallbacks()
        .then(this.sortAndStoreCallbacks)
        .catch(console.log);
    },

    sortAndStoreCallbacks(callbacks) {
      this.apiCallbacks = callbacks;
      this.setCallbacks(callbacks.sort(this.sortCallbacksByDueDate).map(this.createCallbackItem));
      return this.apiCallbacks;
    },

    sortCallbacksByDueDate(callbackA, callbackB) {
      const t1 = moment(callbackA.customerRequestStartTimeWithTimeZoneOffset).diff(Date.now());
      const t2 = moment(callbackB.customerRequestStartTimeWithTimeZoneOffset).diff(Date.now());
      return t1 === t2 ? 0 : t1 > t2 ? 1 : -1;
    },

    /**
     * Creates an EcsNotificationItem for a callback
     * @param cb - The callback object from ECS
     * @returns {object}
     */
    createCallbackItem(cb) {
      if (!cb) {
        return null;
      }

      const callback = { ...cb };

      callback._type = 'CALLBACK_NOTIFICATION';
      callback.id = cb.interactionId;
      callback.iconColor = 'var(--v-xtium-robin-egg-blue)';

      if (cb.assignedAgentId && cb.assignedAgentName) {
        callback.notificationType = 'callback.agentCallback';
        callback.icon = 'mdi-account';
      } else if (cb.assignedTeamId && cb.assignedTeamName) {
        callback.notificationType = 'callback.teamCallback';
        callback.icon = 'mdi-account-multiple';
      }

      callback.timestamp = cb.creationTime;
      callback.timeAgo = moment(cb.creationTime).fromNow();

      callback.headline = {
        i18nKey: 'callback.headline',
        date: this.fromNow(cb.customerRequestStartTimeWithTimeZoneOffset || cb.startTime, cb.customerTimeZoneName)
      };

      const customerName = callback.customerFullName
        ? callback.customerFullName.replace(/^\w/, c => c.toUpperCase())
        : '';
      // Get the message body
      callback.message = this.getCallbackMessageBody(cb, customerName);
      callback.messageVars = this.getCallbackMessageVars(cb, customerName);

      callback.actions = [
        {
          name: 'callback.actions.callNow',
          action: 'initiateCallback'
        }
      ];

      return callback;
    },

    /**
     * Creates the message body for the callback based on time
     * Callbacks closer to 'now' are more brief, while those farther away
     * are more verbose.
     * @param callback
     * @param customerName
     * @returns {string}
     */
    getCallbackMessageBody(callback, customerName) {
      let message = '';
      const callbackTargetDate = callback.customerRequestStartTimeWithTimeZoneOffset || callback.startTime;
      const difference = moment(callbackTargetDate).diff(this.getCurrentTime(), 'days');

      if (difference < 1) {
        // Today
        message = customerName ? 'callback.body.todayCustomer' : 'callback.body.today';
      } else if (difference >= 1 && difference <= 7) {
        // This Week
        message = customerName ? 'callback.body.thisWeekCustomer' : 'callback.body.thisWeek';
      } else {
        // Use a more verbose startDate (and capitalize the first letter)
        message = customerName ? 'callback.body.thisMonthCustomer' : 'callback.body.thisMonth';
      }

      return message;
    },

    getCallbackMessageVars(callback, customerName) {
      const appTimeZone = this.availableTimeZones.find(tz => tz.value === this.applicationSettings.timeZoneId) || {};
      const customerTimeZone = callback.customerTimeZoneName;
      const startTime = this.fixTzOffset(
        callback.customerRequestStartTimeWithTimeZoneOffset,
        this.getIanaTimeZone(customerTimeZone)
      );

      const customerStartTime = moment(startTime).tz(this.getIanaTimeZone(customerTimeZone));
      const agentStartTime = moment(startTime).tz(this.getIanaTimeZone(appTimeZone.id));

      return {
        customerName,
        destination: callback.remoteParty,
        startDate: customerStartTime && customerStartTime.format('dddd, MMMM Do YYYY'),
        startDay: customerStartTime && customerStartTime.format('dddd'),
        startTime: customerStartTime && customerStartTime.format(this.applicationSettings.timeFormat),
        localStartDate: agentStartTime && agentStartTime.format('dddd, MMMM Do YYYY'),
        localStartDay: agentStartTime && agentStartTime.format('dddd'),
        localStartTime:
          agentStartTime && agentStartTime.format(this.applicationSettings.timeFormat) + ' ' + appTimeZone.name,
        timeZone: customerTimeZone
      };
    },

    /**
     * Routes to the Telephony page and inputs the proper information for an outbound call
     * using the information from the callback
     * @param interactionId
     */
    initiateCallback({ interactionId }) {
      this.opened = false;

      if (!interactionId) {
        this.$root.$emit('snackbar-message', {
          message: this.$t('callbackNotAvailableDueToMissingInteraction'),
          type: 'error'
        });
        return;
      }

      const callbackNow = () => {
        try {
          this.addEventTimingConstraint({ interactionId, constraintNames: ['INTERACTION_OFFERED_TO_AGENT_EVENT'] });
          InteractionService.handleCallbackNow(interactionId).catch(() => {
            this.removeEventTimingConstraint({
              interactionId,
              constraintNames: ['INTERACTION_OFFERED_TO_AGENT_EVENT']
            });
          });
          this.getAndCreateAllCallbacks();
        } catch (error) {
          const interaction = this.getInteraction(interactionId);
          const message = interaction.isHandled
            ? this.$t('callbackNotAvailableDueToHandled', { interactionId })
            : interaction.isDialing || interaction.isHandling || interaction.isOnWrapUp
            ? this.$t('callbackNotAvailableDueToHandling', { interactionId })
            : this.$t('callbackNotAvailable');
          this.$root.$emit('snackbar-message', {
            message,
            type: 'error'
          });
          this.removeEventTimingConstraint({ interactionId, constraintNames: ['INTERACTION_OFFERED_TO_AGENT_EVENT'] });
        }
      };

      if (this.$route.path !== '/calls') {
        this.$router
          .push('calls')
          .then(callbackNow)
          .catch(() => {});
      } else {
        callbackNow();
      }
    },

    /**
     * Signals to EcsCrmCallbackSection to call its open() method
     */
    createNewCallback() {
      this.$root.$emit('open-new-callback-dialog');
    },

    onItemClick(item, action) {
      this[action](item);
    }
  }
};
</script>

<style scoped></style>
